// node_modules
import { FC, useEffect, useState } from "react";
// Components
import { PlusButton } from "Components";
import { CaseIntakeFunction } from "./CaseIntakeFunction";
// Types
import { TActionObjectDTO, TEnvironmentVariableDTO } from "Types";
// styles
import styles from "../intake.module.scss";
// Controllers
import { IntakeFunctionControllerSingleton } from "Controllers";
// Controllers
import { ToastHelperSingleton } from "Helpers";
// Enums
import { SearchPriorityEnum, ToastTypeEnum } from "Enums";

export type TCaseIntakeFunctionsProps = {
    caseId: number,
    isEditMode: boolean,
    actionObjects: TActionObjectDTO[],
    keywords: TEnvironmentVariableDTO[]
};

export const CaseIntakeFunctions: FC<TCaseIntakeFunctionsProps> = ({caseId, isEditMode, actionObjects, keywords}: TCaseIntakeFunctionsProps) => {
    // State
    const [currentActionObjects, setCurrentActionObjects] = useState<TActionObjectDTO[]>([]);
    const [currentKeywords, setCurrentKeywords] = useState<TEnvironmentVariableDTO[]>([]);

    // Logic
    useEffect(() => {
        setCurrentActionObjects(actionObjects);
        setCurrentKeywords(keywords);
    }, [actionObjects, keywords]);

    const intakeFunctionDeletedHandler = (intakeFunctionDeletedId: number): void => {
        const newCurrentActionObjects: TActionObjectDTO[] = [];
        const newCurrentKeywords: TEnvironmentVariableDTO[] = [];

        for (const currentActionObject of currentActionObjects) {
            if (currentActionObject.id !== intakeFunctionDeletedId) {
                newCurrentActionObjects.push(currentActionObject);
            }
        }
        for (const currentKeyword of currentKeywords) {
            if (currentKeyword.id !== intakeFunctionDeletedId) {
                newCurrentKeywords.push(currentKeyword);
            }
        }

        setCurrentActionObjects(newCurrentActionObjects);
        setCurrentKeywords(newCurrentKeywords);
    };

    const createIntakeFunctionAsync = async (): Promise<void> => {
        // call server to create intake function
        const intakeFunctionId: number | undefined =  await IntakeFunctionControllerSingleton
            .createIntakeFunctionAsync(caseId);


        // tell user about create result 
        if (!intakeFunctionId) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to create intake function.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully created intake function.");

            // add action object and keyword to component state
            const actionObject: TActionObjectDTO = {
                id: intakeFunctionId,
                action: "",
                actionSynonyms: [],
                dobject: "",
                objectSynonyms: [],
                searchPriority: SearchPriorityEnum.Must
            };
            const keyword: TEnvironmentVariableDTO = {
                id: intakeFunctionId,
                environment: "",
                searchPriority: SearchPriorityEnum.Should,
                synonyms: []
            };

            setCurrentActionObjects([...currentActionObjects, actionObject]);
            setCurrentKeywords([...currentKeywords, keyword]);
        }
        
        return;
    };

    const getKeywordRelatedToActionObject = (actionObject: TActionObjectDTO): TEnvironmentVariableDTO | undefined => {
        for (const currentKeyword of currentKeywords) {
            if (currentKeyword.id === actionObject.id) {   
                return currentKeyword;
            }
        }

        return undefined;
    };
    
    // Render
    return (
        isEditMode ?
            <div className={styles.intakeItem}>
                <div>
                    <h4>Functions</h4>
                    <h5>Action</h5>
                    <h5>Object</h5>
                    <h5>Environment</h5>
                </div>
                {currentActionObjects.map((actionObject: TActionObjectDTO) => {
                    return (
                        <CaseIntakeFunction
                            key={actionObject.id}
                            caseId={caseId}
                            actionObject={actionObject}
                            keyword={getKeywordRelatedToActionObject(actionObject)}
                            intakeFunctionDeletedHandler={intakeFunctionDeletedHandler} />
                    );
                })}
                <PlusButton 
                    onClick={createIntakeFunctionAsync} />
            </div>
        :   
            actionObjects.length > 0 ?
                <div>
                    {actionObjects.filter((actionObject: TActionObjectDTO) => actionObject.action && actionObject.dobject).length > 0 ?
                            <div className={styles.intakeItem}>
                                <h4>Function(s)</h4>
                                <ul>
                                    {actionObjects
                                    .filter((actionObject: TActionObjectDTO) => actionObject.action && actionObject.dobject)
                                    .map((actionObject: TActionObjectDTO) => {
                                        return (
                                            <li key={actionObject.id}>{actionObject.action} {actionObject.dobject}</li>
                                        );
                                    })}
                                </ul>
                            </div>
                        :
                            null
                    }
                    {keywords.filter((keyword: TEnvironmentVariableDTO) => keyword.environment).length > 0 ?
                            <div className={styles.intakeItem}>
                                <h4>Environment(s)</h4>
                                <ul>
                                    {keywords
                                    .filter((keyword: TEnvironmentVariableDTO) => keyword.environment)
                                    .map((keyword: TEnvironmentVariableDTO) => {
                                        return (
                                            <li key={keyword.id}>{keyword.environment}</li>
                                        );
                                    })}
                                </ul>
                            </div>
                        :
                            null
                    }
                </div>
            :
                null
    );
};